import React from 'react';
import { Row, Col, Table, Accordion, Card } from 'react-bootstrap';
import EstimatePaymentsHistory from './EstimatePaymentsHistory';

const styles = {
  cardContainer: {
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
    padding: '24px',
    marginBottom: '24px',
    fontFamily: '"Inter", "Segoe UI", sans-serif'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #f1f5f9',
    paddingBottom: '16px',
    marginBottom: '20px'
  },
  titleBlock: {
    display: 'flex',
    flexDirection: 'column'
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#0f172a',
    margin: 0
  },
  subtitle: {
    fontSize: '13px',
    color: '#64748b',
    marginTop: '4px',
    fontWeight: '500'
  },
  statusBadge: (status) => {
    let bg = '#fff8e1';
    let color = '#b78103';
    let text = 'Pending Approval';

    if (status === 'ACCEPTED') {
      bg = '#e8f5e9';
      color = '#2e7d32';
      text = 'Approved';
    } else if (status === 'REJECTED') {
      bg = '#fdecea';
      color = '#c62828';
      text = 'Rejected';
    }

    return {
      backgroundColor: bg,
      color: color,
      padding: '4px 14px',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: '600',
      display: 'inline-block',
      border: `1px solid ${status === 'ACCEPTED' ? '#c8e6c9' : status === 'REJECTED' ? '#ffcdd2' : '#ffe082'}`
    };
  },
  infoCard: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '16px',
    height: '100%'
  },
  cardTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#2563eb',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '14px'
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    marginBottom: '8px',
    lineHeight: '1.4'
  },
  infoLabel: {
    color: '#64748b'
  },
  infoValue: {
    fontWeight: '600',
    color: '#1e293b',
    textAlign: 'right',
    paddingLeft: '8px'
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#2563eb',
    marginTop: '24px',
    marginBottom: '12px'
  },
  tableHeader: {
    backgroundColor: '#2563eb',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '600',
    verticalAlign: 'middle',
    border: 'none',
    padding: '12px 16px'
  },
  tableCell: {
    fontSize: '13px',
    color: '#334155',
    verticalAlign: 'middle',
    padding: '12px 16px',
    borderBottom: '1px solid #e2e8f0'
  },
  remarksSection: {
    marginTop: '16px'
  },
  remarksTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#334155',
    marginBottom: '4px'
  },
  remarksText: {
    fontSize: '13px',
    color: '#64748b',
    lineHeight: '1.5'
  },
  summarySection: {
    marginTop: '16px',
    width: '100%',
    maxWidth: '260px',
    marginLeft: 'auto'
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    marginBottom: '8px'
  },
  summaryLabel: {
    color: '#64748b'
  },
  summaryValue: {
    color: '#334155',
    fontWeight: '500'
  },
  balanceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    fontWeight: 'bold',
    borderTop: '2px solid #2563eb',
    paddingTop: '8px',
    marginTop: '8px'
  },
  balanceLabel: {
    color: '#0f172a',
    fontSize: '14px',
    fontWeight: '700'
  },
  balanceValue: {
    color: '#0f172a',
    fontSize: '16px',
    fontWeight: '700'
  }
};

const getServiceTypeLabel = (type) => {
  switch (type) {
    case "AZURO": return "Azuro";
    case "VENDOR_WITH_AZURO_MARGIN": return "Vendor + Azuro Margin";
    case "VENDOR_ONLY": return "Vendor Only";
    default: return type || "-";
  }
};

const getGstTypeLabel = (type) => {
  switch (type) {
    case "GST_REGISTERED": return "GST Registered";
    case "NON_GST_VENDOR": return "Non-GST Vendor";
    default: return type || "-";
  }
};

const getPaymentTypeLabel = (type) => {
  switch (type) {
    case "ADVANCE_PAYMENT": return "Advance";
    case "FULL_PAYMENT": return "Full Payment";
    default: return type || "-";
  }
};

const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return "₹0";
  return "₹" + Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const calculateEstimateTotals = (est) => {
  let subtotal = 0;
  let gst = 0;
  let totalCustomerPays = 0;

  (est.costEstimateItems || []).forEach(item => {
    const qty = Number(item.qty || 1);
    let baseCost = Number(item.amount || 0);
    const markup = Number(item.margin || 0);
    const gstPct = Number(item.gstPercentage || 0);


    const lineSubtotal = qty * (baseCost + markup);  // qty × unit price
    const lineTax = (lineSubtotal * gstPct) / 100;   // GST on this line

    subtotal += lineSubtotal;
    gst += lineTax;
    totalCustomerPays += lineSubtotal + lineTax;
  });

  const advancePayment = Number(est.advancePaymentAmount || 0);
  const balanceAmount = totalCustomerPays;

  return {
    subtotal,          // sum of (qty × unit price) across all items
    gst,               // total GST across all items
    advancePayment,
    balanceAmount,
    totalCustomerPays  // subtotal + gst
  };
};

export const EstimateDetails = ({ estimates, serviceRequest }) => {

  console.log("estimates: " + JSON.stringify(estimates));
  console.log("serviceRequest: " + JSON.stringify(serviceRequest));

  if (!estimates || estimates.length === 0) {
    return <div className="text-muted text-center py-4">No estimates found.</div>;
  }

  const categoryName = serviceRequest?.serviceRequestCategory?.name || "Repair & Maintenance";
  const serviceRequestId = serviceRequest?.id || "";

  return (
    <Accordion>
      {estimates.map((est, idx) => {
        const totals = calculateEstimateTotals(est);
        const VendorName = est.vendorName;
        const eventKey = String(est.id || idx);

        return (
          <Card key={est.id || idx} style={{ border: '1px solid #e2e8f0', borderRadius: '12px', marginBottom: '16px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
            <Accordion.Toggle
              as={Card.Header}
              eventKey={eventKey}
              style={{
                cursor: 'pointer',
                backgroundColor: '#f8fafc',
                border: 'none',
                borderBottom: '1px solid #e2e8f0',
                padding: '14px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                userSelect: 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <span style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>
                  Estimate #{est.id || `Draft-${idx + 1}`}
                </span>
                <span style={{ color: '#cbd5e1', fontSize: '18px' }}>|</span>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#64748b' }}>
                  Vendor: {VendorName}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  backgroundColor: est.approvalStatus === 'ACCEPTED' ? '#e8f5e9' : est.approvalStatus === 'REJECTED' ? '#fdecea' : '#fff8e1',
                  color: est.approvalStatus === 'ACCEPTED' ? '#2e7d32' : est.approvalStatus === 'REJECTED' ? '#c62828' : '#b78103',
                  padding: '3px 12px',
                  borderRadius: '20px',
                  fontSize: '11px',
                  fontWeight: '700',
                  border: `1px solid ${est.approvalStatus === 'ACCEPTED' ? '#c8e6c9' : est.approvalStatus === 'REJECTED' ? '#ffcdd2' : '#ffe082'}`
                }}>
                  {est.approvalStatus === 'ACCEPTED' ? 'Approved' : est.approvalStatus === 'REJECTED' ? 'Rejected' : 'Pending Approval'}
                </span>
                <span className="text-muted" style={{ fontSize: '10px' }}>▼</span>
              </div>
            </Accordion.Toggle>

            <Accordion.Collapse eventKey={eventKey}>
              <Card.Body style={{ padding: '24px', backgroundColor: '#fff', borderTop: 'none' }}>
                {/* Details Header Info */}
                <div style={{ display: 'none' }}>
                  {categoryName} • Service Request #{serviceRequestId}
                </div>

                {/* Information Cards Row */}
                <Row className="mb-4">
                  {/* Customer Info */}
                  <Col md={4} className="mb-3 mb-md-0">
                    <div style={styles.infoCard}>
                      <div style={styles.cardTitle}>Customer</div>
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Name</span>
                        <span style={styles.infoValue}>{est.customerName || "-"}</span>
                      </div>
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Address</span>
                        <span style={styles.infoValue}>{est.address || "-"}</span>
                      </div>
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>GSTIN</span>
                        <span style={styles.infoValue}>{est.gstin || "-"}</span>
                      </div>
                    </div>
                  </Col>

                  {/* Service Info */}
                  <Col md={4} className="mb-3 mb-md-0">
                    <div style={styles.infoCard}>
                      <div style={styles.cardTitle}>Service</div>
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Type</span>
                        <span style={styles.infoValue}>{getServiceTypeLabel(est.serviceType)}</span>
                      </div>
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>GST</span>
                        <span style={styles.infoValue}>{getGstTypeLabel(est.gstType)}</span>
                      </div>
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Payment</span>
                        <span style={styles.infoValue}>{getPaymentTypeLabel(est.paymentType)}</span>
                      </div>
                    </div>
                  </Col>

                  {/* Quick Summary Info */}
                  <Col md={4}>
                    <div style={styles.infoCard}>
                      <div style={styles.cardTitle}>Estimate Summary</div>
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Items</span>
                        <span style={styles.infoValue}>{(est.costEstimateItems || []).length}</span>
                      </div>
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}><b>Expected Advance</b></span>
                        <span style={styles.infoValue}>{formatCurrency(totals.advancePayment)}</span>
                      </div>
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>Total Balance</span>
                        <span style={styles.infoValue}>{formatCurrency(totals.balanceAmount)}</span>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Estimate Items Heading */}
                <div style={styles.sectionTitle}>Estimate Items</div>

                {/* Table */}
                <div className="table-responsive">
                  <Table bordered style={{ borderCollapse: 'collapse', border: '1px solid #e2e8f0', width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.tableHeader, textAlign: 'left', width: '30%' }}>Item</th>
                        <th style={{ ...styles.tableHeader, width: '10%' }}>Qty</th>
                        <th style={{ ...styles.tableHeader, width: '12%' }}>Amount</th>
                        <th style={{ ...styles.tableHeader, width: '12%' }}>Margin</th>
                        <th style={{ ...styles.tableHeader, width: '12%' }}>Price</th>
                        <th style={{ ...styles.tableHeader, width: '10%' }}>GST</th>
                        <th style={{ ...styles.tableHeader, textAlign: 'right', width: '14%' }}>Customer Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(est.costEstimateItems || []).map((item, itemIdx) => {
                        const qty = Number(item.qty || 1);
                        let baseCost = Number(item.amount || 0);
                        let markup = Number(item.margin || 0);

                        const price = baseCost + markup;
                        const total = price * qty * (1 + Number(item.gstPercentage || 0) / 100);

                        return (
                          <tr key={item.id || itemIdx}>
                            <td style={{ ...styles.tableCell, textAlign: 'left' }}>{item.name || "-"}</td>
                            <td style={{ ...styles.tableCell, textAlign: 'center' }}>{qty}</td>
                            <td style={{ ...styles.tableCell, textAlign: 'center' }}>{formatCurrency(baseCost)}</td>
                            <td style={{ ...styles.tableCell, textAlign: 'center' }}>{formatCurrency(item.margin || 0)}</td>
                            <td style={{ ...styles.tableCell, textAlign: 'center' }}>{formatCurrency(price)}</td>
                            <td style={{ ...styles.tableCell, textAlign: 'center' }}>{item.gstPercentage || 0}%</td>
                            <td style={{ ...styles.tableCell, textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(total)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                {/* Bottom Row: Remarks & Financials Summary */}
                <Row className="mt-3">
                  <Col md={7}>
                    <div style={styles.remarksSection}>
                      <div style={styles.remarksTitle}>Remarks</div>
                      <div style={styles.remarksText}>
                        {est.approvalStatus === 'ACCEPTED'
                          ? "This estimate has been approved by the approver."
                          : est.approvalStatus === 'REJECTED'
                            ? "This estimate has been rejected by the approver."
                            : "Estimate sent to the tenant. Waiting for approval."
                        }
                      </div>
                    </div>
                  </Col>

                  <Col md={5}>
                    <div style={styles.summarySection}>
                      {/* <div style={styles.summaryRow}>
                        <span style={styles.summaryLabel}>Subtotal</span>
                        <span style={styles.summaryValue}>{formatCurrency(totals.subtotal)}</span>
                      </div>
                      <div style={styles.summaryRow}>
                        <span style={styles.summaryLabel}>GST</span>
                        <span style={styles.summaryValue}>{formatCurrency(totals.gst)}</span>
                      </div>
                      {totals.advancePayment > 0 && (
                        <div style={{ ...styles.summaryRow }}>
                          <span style={{ ...styles.summaryLabel, color: '#059669' }}>Advance Payment</span>
                          <span style={{ ...styles.summaryValue, color: '#059669' }}>- {formatCurrency(totals.advancePayment)}</span>
                        </div>
                      )} */}
                      <div style={styles.balanceRow}>
                        <span style={styles.balanceLabel}>Balance Amount</span>
                        <span style={styles.balanceValue}>{formatCurrency(totals.balanceAmount)}</span>
                      </div>
                    </div>
                  </Col>

                </Row>
                <hr style={{ margin: '24px 0', borderColor: '#cbd5e1' }} />
                <EstimatePaymentsHistory payments={est.payments || []} />
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        );
      })}
    </Accordion>
  );
};

export default EstimateDetails;
